import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { RemoteDataWrapperService } from './remote-data-wrapper.service';

export type RealObjectStructure = {
  [key: string]: any;
};

export interface MachineFunction {
  name: string;
  id: number;
  args: Array<{Name: string, Type: string}> | null;
  role?: string;
};

export interface MachineSetterProperty {
  id: number;
  name: string;
}


@Injectable()
export class MachineOneService implements OnDestroy {

  private _unsubscriber = new Subject<void>();
  public machineVariables: Map<number, BehaviorSubject<string | boolean | number | null | any[]>> = new Map<number, BehaviorSubject<string | boolean | number | null | any[]>>();

  private _realObject: BehaviorSubject<RealObjectStructure | null> = new BehaviorSubject<RealObjectStructure | null> (null);
  public machineStructure$: Observable<any | null> = this._realObject.asObservable();

  private _availableFunctions: MachineFunction[] = [];

  private _availableSetters: MachineSetterProperty[] = [];

  /**
   * Available machine structure from hub.
   *
   * @private
   * @type {Subject<string>}
   * @memberof RemoteDataClientService
   */
  private _machineStructureRaw: BehaviorSubject<string> = new BehaviorSubject<string>('{}');
  public machineStructureRaw$: Observable<string> = this._machineStructureRaw.asObservable();

  constructor(
  ) {
  }

  public initialize(str: string): void {
    try {
      if (str !== '{}') {
        // important to delete old subjects
        this.machineVariables = new Map<number, BehaviorSubject<string | boolean | number | null | any[]>>();
        this._availableFunctions = [];
        this._realObject.next(this._constructRealObject(JSON.parse(str)));
        this._machineStructureRaw.next(str);
      }
    } catch {
      this._constructRealObject(null);
    }

    console.log(this._availableFunctions);
  }

  ngOnDestroy(): void {
    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  public updateVariable(variableId: number, value: string | boolean | number | null): void {
    if (this.machineVariables.has(variableId)) {
      this.machineVariables.get(variableId)!.next(value);
    }
  }

  public getFunctionByName(name: string): MachineFunction | undefined {
    return this._availableFunctions.find(i => i.name === name);
  }

  public getFunctionById(id: number): MachineFunction | undefined {
    return this._availableFunctions.find(i => i.id === id);
  }

  public getSetterById(id: number): MachineSetterProperty | undefined {
    return this._availableSetters.find(s => s.id === id);
  }

  public checkCallingFuncArguments(func: MachineFunction, args: any[] | null): boolean {
    if (func.args?.length === 0 && args === null) return true;

    return func.args?.length === args?.length;
  }

  private _constructRealObject(input: any): RealObjectStructure {
    const result: RealObjectStructure = {};

    for (const key in input) {
      if (input[key] && typeof input[key] === 'object') {
        if (key === "_V") {
          // If _V contains an array, recursively process each element
          if (Array.isArray(input[key])) {
            return input[key].map((element: any) => this._constructRealObject(element));
          } else {
            // Otherwise, return the value directly
            return input[key];
          }
        } else if (Object.keys(input[key]).includes('Args')) {
          this._availableFunctions.push({
            name: key,
            id: input[key]['_ID'],
            args: input[key]['Args']
          });
        } else if (Object.keys(input[key]).includes('_T') && input[key]['_T'] === 'CMD') {
          this._availableFunctions.push({
            name: key,
            id: input[key]['_ID'],
            args: null,
            role: input[key]['Role']
          });
        } else if (Object.keys(input[key]).includes('_RO') && input[key]['_RO'] === false) {
          // this branch is for not readonly properties
          this._availableSetters.push({
            name: key,
            id: input[key]['_ID']
          });

          result[key] = this._constructRealObject(input[key]);
        } else {
          // For other properties, recursively process them
          result[key] = this._constructRealObject(input[key]);
        }
      } else if (key === "_V") {
        const subject: BehaviorSubject<any> = new BehaviorSubject(input[key]);
        this.machineVariables.set(input['_ID'] ?? -1, subject);
        return subject.asObservable();
      } else if (key === "_ID" || key === "_L" || key === "_N" || key === "_T") {
        // Skip meta properties unless they are needed directly in the result
        continue;
      }
    }

    return result;
  }
}
